import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom, Subscription, tap } from 'rxjs';
import {
  usersManageApi,
  resendEmailActivationLink,
} from 'src/app/shared/constants/api.constants';
import { Toast } from 'src/app/shared/enums/toast';
import { IUser } from '../../shared/models/IUser.model';
import { UserService } from '../user.service';
import { HelpersService, HttpBaseService, ToastService } from '@evento-ng/libs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-personal-data',
  templateUrl: './personal-data.component.html',
  styleUrls: ['./personal-data.component.scss'],
})
export class PersonalDataComponent implements OnInit, OnDestroy {
  PersonalInfoForm: UntypedFormGroup;
  editPasswordForm: UntypedFormGroup;
  showPersonalInfo: boolean;
  showEditPassword: boolean;
  user: IUser;

  subs: Subscription[] = [];

  kioskModeOptions = [
    {
      id: true,
      name: 'ActiveMode',
    },
    {
      id: false,
      name: 'InactiveMode',
    },
  ];
  constructor(
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private dataService: HttpBaseService,
    private spinner: NgxSpinnerService,
    private toast: ToastService,
    private titleService: Title,
    public helpers: HelpersService,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.userService.selectedUser$
        .pipe(
          tap((user) => {
            if (user) {
              this.user = user;
              this.titleService.setTitle(
                user?.firstName + ' ' + user?.lastName
              );
            }
            this.initPersonalInfoForm();
          })
        )
        .subscribe()
    );
    this.initEditPasswordForm();
  }

  save(): void {
    this.spinner.show();
    const { firstName, lastName, kioskMode } = this.PersonalInfoForm.value;
    firstValueFrom(
      this.dataService
        .patch(`${usersManageApi}/personal-information/${this.user.id}`, {
          firstName,
          lastName,
          kioskMode,
        })
        .pipe(
          tap((user: IUser) => {
            this.userService.selectedUser$.next(user);
            this.spinner.hide();
            this.showPersonalInfo = false;
            this.toast.show(
              'Success',
              this.translate.instant('updated successfully'),
              {
                classname: Toast.success,
              }
            );
          })
        )
    );
  }

  private initPersonalInfoForm(): void {
    let firstName;
    let lastName;
    let email;
    let mobile;
    let kioskMode;
    if (this.user) {
      firstName = this.user.firstName;
      lastName = this.user.lastName;
      email = this.user.email;
      mobile = this.user.mobile;
      kioskMode =
        this.user?.kioskMode == undefined || !this.user?.kioskMode
          ? this.kioskModeOptions[1].id
          : this.kioskModeOptions[0].id;
    }
    this.PersonalInfoForm = this.formBuilder.group({
      firstName: [firstName, Validators.required],
      lastName: [lastName, Validators.required],
      email: [email],
      mobile: [mobile],
      kioskMode: [kioskMode, Validators.required],
    });
  }

  changePassword(): void {
    this.spinner.show();
    firstValueFrom(
      this.dataService
        .patch(
          `${usersManageApi}/password/${this.user.id}`,
          this.editPasswordForm.value
        )
        .pipe(
          tap((_) => {
            this.spinner.hide();
            this.editPasswordForm.reset();
            this.toast.show(
              'Success',
              this.translate.instant('Password has been changed'),
              {
                classname: Toast.success,
              }
            );
          })
        )
    );
  }
  private initEditPasswordForm(): void {
    this.editPasswordForm = this.formBuilder.group(
      {
        password: ['', Validators.required],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: [this.MustMatch('password', 'confirmPassword')],
      }
    );
  }
  MustMatch(controlName: string, matchingControlName: string): any {
    return (formGroup: UntypedFormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        // return if another validator has already found an error on the matchingControl
        return;
      }

      // set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  resendActivationLink(): void {
    this.spinner.show();
    firstValueFrom(
      this.dataService
        .post(
          `${resendEmailActivationLink}/${this.user.id}/resend-activation-email`,
          {}
        )
        .pipe(
          tap((_) => {
            this.spinner.hide();
            this.toast.show(
              'Success',
              this.translate.instant('Activation link has been sent'),
              {
                classname: Toast.success,
              }
            );
          })
        )
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
